<template>
    <section
        ref="section"
        :class="['slice slice-faq slice-faq--default', {'slice-faq--hide-in-mobile': slice.primary.hideInMobile}]"
        aria-label="faq"
        data-testid="slice-faq-default"
    >
        <div class="slice-content slice-faq--default__content">
            <svg
                v-if="slice.primary.withShapes"
                class="slice-faq--default__shape"
                width="1212"
                height="696"
                viewBox="0 0 1212 696"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                focusable="false"
            >
                <path
                    d="M1035.26 396.067C993.766 293.072 898.041 306.198 830.242 333.327L189.729 589.616C121.93 616.746 41.489 667.815 85.1653 776.229C128.839 884.644 222.381 866.099 290.181 838.969L930.693 582.676C998.491 555.546 1076.75 499.055 1035.26 396.067Z"
                    fill="#FC5757"
                />
                <path
                    d="M1257.87 256.384C1169.4 165.448 1074.54 221.962 1015.13 280.929L453.871 837.994C394.463 896.966 332.204 986.984 425.33 1082.7C518.458 1178.43 608.654 1117.13 668.065 1058.16L1229.33 501.088C1288.74 442.127 1346.34 347.319 1257.87 256.384Z"
                    fill="#FF7370"
                />
                <path
                    d="M1479.73 47.8154C1348.39 -26.9281 1258.75 70.3213 1210.43 158.377L753.943 990.263C705.624 1078.32 664.362 1203.11 802.603 1281.78C940.844 1360.46 1023.58 1259.28 1071.9 1171.23L1528.38 339.333C1576.7 251.278 1611.05 122.56 1479.73 47.8154Z"
                    fill="#FF91F0"
                />
            </svg>

            <PrismicRichText
                v-if="isFilled.richText(slice.primary.title)"
                :field="slice.primary.title"
                class="slice-faq--default__title mui-title"
                data-testid="slice-faq-title"
            />
            <PrismicRichText
                v-if="isFilled.richText(slice.primary.description)"
                :field="slice.primary.description"
                class="slice-faq--default__description slice-description"
                data-testid="slice-faq-description"
            />
            <div class="slice-faq--default__items">
                <MktFaqCollapsible
                    v-for="(item, index) in faqItems"
                    :id="asText(item.questionTitle)"
                    :key="`faq-item-${index}`"
                    class="slice-faq--default__item"
                >
                    <template #header>
                        <PrismicRichText
                            v-if="isFilled.richText(item.questionTitle)"
                            :field="item.questionTitle"
                            class="slice-faq--default__question-title"
                        />
                    </template>
                    <template #content>
                        <PrismicRichText v-if="isFilled.richText(item.answer)" :field="item.answer" class="slice-faq--default__answer" />
                    </template>
                </MktFaqCollapsible>
            </div>
            <div v-if="isFilled.link(slice.primary.link)" class="slice-faq--default__link-container">
                <MuiLink
                    v-if="isFilled.link(slice.primary.link)"
                    :href="$withLocalePrefix(slice.primary.link.url || '')"
                    :text="slice.primary.link.text || ''"
                    data-component-source="faq"
                />
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {asText, isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {FaqSlice} from '@prismicio-types';
    import {MktFaqCollapsible, MuiLink} from '#components';
    import {useComponentTracking} from '#imports';

    type FaqDefaultVariation = Extract<FaqSlice, {variation: 'default'}>;

    const props = defineProps(getSliceComponentProps<FaqDefaultVariation>());

    const section = ref(null);

    const faqItems = computed(() => props.slice.primary.items ?? []);

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-faq {
        &--hide-in-mobile {
            display: none;

            @include mq.screen_sm(min) {
                display: block;
            }
        }

        &--default {
            position: relative;

            .slice-content {
                position: relative;
                text-align: left;
                max-width: 100%;
                border-radius: 20px;
                overflow: hidden;
                padding: var(--joy-core-spacing-4);

                @include mq.screen_xs(min) {
                    position: static;
                    overflow: visible;
                    padding: var(--joy-core-spacing-12) var(--joy-core-spacing-9);
                }

                @include mq.screen_sm(min) {
                    max-width: 865px;
                }
            }

            &__shape {
                position: absolute;
                bottom: -20px;
                right: 0;
                z-index: 0;
                height: auto;
                pointer-events: none;

                @include mq.screen_md(min) {
                    width: 65vw;
                }
            }

            &__title,
            &__description,
            &__items,
            &__link-container {
                position: relative;
                z-index: 1;
            }

            &__title,
            &__description {
                padding-left: var(--joy-core-spacing-8);
            }

            &__title {
                margin-bottom: var(--joy-core-spacing-8);
            }

            &__link-container {
                display: flex;
                justify-content: center;
                margin-top: var(--joy-core-spacing-4);

                @include mq.screen_sm(min) {
                    margin-bottom: 0;
                }
            }

            &__items {
                margin-top: var(--joy-core-spacing-8);
                width: 100%;
                display: flex;
                flex-direction: column;
                text-align: left;
                padding: var(--joy-core-spacing-8) var(--joy-core-spacing-8) 0 var(--joy-core-spacing-8);
                border-radius: 20px;
                background-color: var(--joy-color-neutral-0);

                @include mq.screen_sm(max) {
                    margin-top: var(--joy-core-spacing-6);
                }
            }

            &__item {
                border-bottom: 1px solid var(--joy-color-neutral-30);

                &:last-child {
                    border: none;
                }

                .faq-collapsible__header {
                    padding: var(--joy-core-spacing-6) 0;
                }
            }

            &__question-title {
                margin-right: var(--joy-core-spacing-4);

                h2,
                h3 {
                    color: var(--joy-color-neutral-60);
                    @include text.getFontBySize(primary-850);
                    letter-spacing: -0.2px;
                    text-align: left;
                }
            }

            &__answer {
                margin-bottom: var(--joy-core-spacing-7);
                @include text.getFontBySize(primary-400);
                color: var(--joy-color-neutral-40);

                a,
                a:visited {
                    color: var(--joy-color-secondary-30);
                    font-weight: var(--joy-font-weight-bold);
                }

                a:hover {
                    text-decoration: underline;
                }

                p:not(:first-child) {
                    margin-top: var(--joy-core-spacing-4);
                }

                ul,
                ol {
                    margin: var(--joy-core-spacing-8) var(--joy-core-spacing-4);
                    margin-right: 0;
                }
            }
        }
    }
</style>
